import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Skeleton } from "antd";

import KindSumNav from "@Components/agent/KindSumNav";
import Crumbs from "@Components/Crumbs";
import ReportIndex from "@Components/kindsum/New/Report/index";
import FrontPageNews from "@Components/kindsum/New/FrontPageNews";
import KindSumScore from "@Components/kindsum/New/KindSumScore";
import { fetchKindsPcInfo, fetchRecommendUsedCar } from "@Services/kindsum";
import { IKindsPcInfo, IUserCarItem } from "@Types/kindsum";
import KindSumRightInquiry from "@Components/kindsum/KindSumRightInquiry";
import KindSumMain from "@Components/kindsum/KindSumMain";
import HistoryIframe from "@Components/kindsum/historyIframe";
import { host } from "@Services/url";
import withLayout from "../../layouts/withLayout_v2";

import { delayFetch } from "@Lib/delayFetch";

const NewDetail = React.lazy(() => import("./newDetail"));

const initData: IKindsPcInfo = {
  factoryDiscount: {
    cashDiscount: "",
    discountRemark: "",
    expiredAt: "",
    giftDiscount: "",
    insuranceDiscount: "",
    loanDiscount: "",
    maintenanceDiscount: "",
    otherDiscount: "",
    remark: "",
    subsidyDiscount: "",
    warrantyDiscount: "",
    roster: [],
    title: "",
  },
  bgFreeThumb: "",
  brandId: 0,
  brandLogo: "",
  brandName: "",
  saleMonth: 0,
  commentCount: 0,
  commentScore: 0,
  usedCarSaleCount: 0,
  energyType: 0,
  favorite: false,
  importantEquipSpecs: {
    airBag: [],
    autoType: [],
    batteryLife: [],
    electricConsumption: [],
    fuelConsumption: [],
    intake: [],
    motorMaxPower: [],
    maxPower: [],
    gearBox: [],
  },
  information: {
    movieNum: 0,
    newsNum: 0,
    shortsNum: 0,
    trialNum: 0,
  },
  inquiry: {
    discountPrice: 0,
    inquiryNum: 0,
    inquiryTitle: "",
    discountTitle: "",
    inquiryMessage: "",
    isInquiry: -1,
    minimumPrice: "",
    showroomNum: 0,
  },
  kindId: 0,
  kindName: "",
  newKindId: 0,
  pictures: {
    colorPictures: [],
    pictureCounts: {
      count: 0,
      insideNum: 0,
      outsideNum: 0,
      spaceNum: 0,
    },
  },
  price: "",
  rank: 0,
  saleCount: 0,
  saleStop: 0,
  superTestModelYearId: 0,
  tax: {
    fuelTax: "",
    licenseTax: "",
    tax: "",
  },
  thumb: "",
  usedCarLink: "",
  superTestModelYearName: "",
  visits: 0,
  willSale: 0,
  year: 0,
  superTest: {
    comfortModelYearId: 0,
    comfortModelYearName: "",
    performanceModelYearId: 0,
    performanceModelYearName: "",
    spaceModelYearId: 0,
    spaceModelYearName: "",
    safeEquipModelYearId: 0,
    safeEquipModelYearName: "",
  },
  rosterRelated: {
    button: "",
    link: "",
    message: "",
  },
  tcDiscount: "",
};

interface IAdProps {
  data?: {
    link: string;
    img: string;
    video?: string;
  };
}

const Information = () => {
  const locationRef = useRef(null);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [load, setLoad] = useState<boolean>(false);

  const { data = initData } = useQuery<IKindsPcInfo>([`fetchKindsPcInfo`, window.query_kind_id], async () => {
    const res = await fetchKindsPcInfo({ kindId: window.query_kind_id });
    // 处理 factoryDiscount.extra 数组，添加 label 字段
    if (res.data?.factoryDiscount?.extra?.length) {
      res.data.factoryDiscount.extra = res.data.factoryDiscount.extra.map((item: any) => ({
        ...item,
        label: item.key, // 默认使用 key 作为 label，如果需要其他映射关系可以在这里修改
      }));
    }
    setIsMount(true);
    return res?.status === 12000 ? res.data : initData;
  });
  
  const { data: userCarData = [], refetch } = useQuery<Array<IUserCarItem>>(
    [`fetchRecommendUsedCar`, window.query_kind_id],
    async () => {
      const res: { data; status } = await fetchRecommendUsedCar({ kindId: window.query_kind_id, limit: 8 });
      return res?.status === 12000 ? res?.data.items : [];
    },
  );

  useEffect(() => {
    delayFetch(() => {
      setLoad(true);
      refetch();
    });
    // 防止不加载
    setTimeout(() => {
      if (!load) setLoad(true);
    }, 3000);
  }, []);

  const ReportItem = useMemo(() => {
    if (!isMount || !data?.kindId)
      return (
        <div className='flex flex-col px-12 pt-16 items-center justify-start'>
          <Skeleton style={{ width: "260px", height: "350px" }} active={false} />
        </div>
      );
    if (
      data?.superTest.comfortModelYearId ||
      data?.superTest.performanceModelYearId ||
      data?.superTest.safeEquipModelYearId ||
      data?.superTest.spaceModelYearId
    )
      return <ReportIndex data={data.superTest} />;
    return <FrontPageNews />;
  }, [data]);

  return (
    <div className='bg-white'>
      <div className='w-1190 mx-auto'>
        {/* 位置栏 */}
        <Crumbs
          style={{ paddingTop: "0", height: "44px" }}
          data={[
            {
              value: "首頁",
              url: "https://www.8891.com.tw",
            },
            {
              value: "找車",
              url: `${host.c}/Models`,
            },
            {
              value: data.brandName,
              url: `${host.c}/Models/${data.brandName.toLowerCase() || "廠牌"}`,
            },
            {
              value: data.kindName,
              // url: `${host.c}/photoModel-info.html?k=${window.kindId || data.kindId}`,
            },
          ]}
        />
      </div>
      <div className='w-1190 mx-auto sticky top-[116px] z-99'>
        {/* 引入頁面導航 */}
        <KindSumNav
          isNavSticky={true}
          kindId={String(data.kindId)}
          brandIds={String(data.brandId)}
          brandName={data.brandName}
          kindName={data.kindName}
          newKindId={String(data.newKindId)}
          css={{ marginBottom: "0" }}
        />
      </div>

      <div
        className='w-full py-10 min-h-492 min-w-1190'
        style={{ background: "linear-gradient(180deg,#f7faff, #e4ebfb)" }}
      >
        <div className='w-1190 mx-auto'>
          <div className='flex justify-between'>
            <div className='z-1'>
              {/* 实测数据、无实测(id：25691) */}
              <div className='h-410 bg-white rounded-4'>{ReportItem}</div>
              {/* 車友交流 */}
              <KindSumScore
                dataSource={{
                  count: data.commentCount,
                  score: data.commentScore,
                  brandName: data.brandName,
                  kindName: data.kindName,
                }}
              />
            </div>
            {/* 中間基礎信息、配備 */}
            <KindSumMain data={data} />
            {/* 右侧询价逻辑 */}
            <KindSumRightInquiry data={data} />
          </div>
        </div>
      </div>
      {load && (
        <div className='w-1190 mx-auto' ref={locationRef}>
          <React.Suspense
            fallback={
              <div className='w-full min-h-100 flex items-center justify-center py-20'>
                <Skeleton active={false} />
              </div>
            }
          >
            <NewDetail data={data} />
          </React.Suspense>

          {/* 浏览车款储存记录 */}
          {!!data.kindId && <HistoryIframe data={data} />}
        </div>
      )}
    </div>
  );
};

withLayout({
  cmp: <Information />,
  params: {
    AdTopBanner: true,
    nav: "DefaultFixedNav",
    footer: "Footer",
    appDownload: false,
  },
});
